/*
 * The notifications banner's memory.
 *
 * Kept away from the component because the only part that can be wrong is the
 * arithmetic, and the arithmetic is testable while a React tree reading
 * localStorage is not.
 */

const KEY = 'picksix.notifyBannerDismissedAt'

/** A week of quiet after somebody closes it. */
export const BANNER_QUIET_MS = 7 * 24 * 60 * 60 * 1000

/**
 * Whether to draw the banner, given whatever was in storage.
 *
 * FAILS OPEN, always. Absent, empty, unparseable, or a timestamp in the future
 * all show the banner, because the failure that hides a prompt is the one
 * nobody reports - the symptom is silence, and silence is what this whole
 * feature exists to fix.
 *
 * The future case is not hypothetical: a phone whose clock was wrong when the
 * tap landed, or was corrected afterwards, would otherwise sit inside a quiet
 * period with no end.
 */
export function shouldShowBanner(dismissedAt: string | null, now: number): boolean {
  if (dismissedAt === null) return true

  const at = Number(dismissedAt)
  if (!Number.isFinite(at) || dismissedAt.trim() === '') return true
  if (at > now) return true

  return now - at >= BANNER_QUIET_MS
}

/**
 * The stored dismissal, or null.
 *
 * Every accessor is guarded: a private window, cleared site data, or a browser
 * set to refuse storage all throw on read rather than returning empty, and a
 * banner is not worth a blank screen.
 */
export function readDismissed(): string | null {
  try {
    return window.localStorage.getItem(KEY)
  } catch {
    return null
  }
}

export function writeDismissed(now: number): void {
  try {
    window.localStorage.setItem(KEY, String(now))
  } catch {
    /* Storage refused. The banner returns next load, which is the safe side. */
  }
}
